import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  OnChanges,
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import { AffairModel } from '../../../../core/models/affair.model'
import { CheckboxModule } from 'primeng/checkbox'
import { ChipModule } from 'primeng/chip'
import { CardModule } from 'primeng/card'
import { FormsModule } from '@angular/forms'
import { ButtonModule } from 'primeng/button'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { TableModule } from 'primeng/table'
import { TooltipModule } from 'primeng/tooltip'
import { BadgeModule } from 'primeng/badge'
import { MessagesModule } from 'primeng/messages'
import { MessageModule } from 'primeng/message'
import { DividerModule } from 'primeng/divider'
import { ScrollPanelModule } from 'primeng/scrollpanel'
import { KeyValuePipe } from '@angular/common'
import { AffairOfficeFileComponent } from '../../../affairs/components/affair-schedule-products-select/affair-office-file/affair-office-file.component'
import { environment } from '../../../../../environments/environment'

@Component({
  selector: 'app-intervention-reschedule-products',
  standalone: true,
  imports: [
    CommonModule,
    CheckboxModule,
    ChipModule,
    CardModule,
    FormsModule,
    ButtonModule,
    ProgressSpinnerModule,
    TableModule,
    TooltipModule,
    BadgeModule,
    MessagesModule,
    MessageModule,
    DividerModule,
    ScrollPanelModule,
    KeyValuePipe,
    AffairOfficeFileComponent,
  ],
  templateUrl: './intervention-reschedule-products.component.html',
  styleUrls: ['./intervention-reschedule-products.component.scss'],
})
export class InterventionRescheduleProductsComponent
  implements OnInit, OnChanges
{
  @Input() affair: AffairModel
  @Input() products: any[] = []
  @Input() loadingProducts: boolean = false
  @Input() restoredSelectedProductIds: number[] = []
  @Input() restoredProductGroup: any = null
  @Input() resetSelection: boolean = false
  @Input() interventionProducts: any[] = [] // Produits de l'intervention originale

  @Output() productSelectionChanged = new EventEmitter<any[]>()
  @Output() productGroupCreated = new EventEmitter<any>()

  // Stockage des produits traités
  uniqueProducts: any[] = []
  selectedProducts: any[] = []
  productGroups: any[] = []

  // Fiche bureau
  fullAffairDetails: any
  fullAffairLoading: boolean = false

  // Ensemble pour stocker les IDs uniques
  private productIdSet = new Set<number>()
  private erpProductIdMap = new Map<string, number>()

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.processAndDeduplicateProducts()
    this.initializeSelectedProducts()
    this.getFullAffairDetails()
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Si les produits sont chargés ou modifiés
    if (changes['products'] && changes['products'].currentValue) {
      this.processAndDeduplicateProducts()
    }

    // Si les ID de produits sélectionnés sont restaurés
    if (
      changes['restoredSelectedProductIds'] &&
      changes['restoredSelectedProductIds'].currentValue &&
      changes['restoredSelectedProductIds'].currentValue.length > 0
    ) {
      this.updateSelectionFromIds()
    }

    // Si le groupe de produits est restauré
    if (
      changes['restoredProductGroup'] &&
      changes['restoredProductGroup'].currentValue
    ) {
      this.restoreProductGroup(changes['restoredProductGroup'].currentValue)
    }

    // Si une réinitialisation est demandée
    if (
      changes['resetSelection'] &&
      changes['resetSelection'].currentValue === true
    ) {
      this.initializeSelectedProducts()
    }

    // Si les produits de l'intervention sont fournis - prioritaire sur tout
    if (
      changes['interventionProducts'] &&
      changes['interventionProducts'].currentValue &&
      changes['interventionProducts'].currentValue.length > 0
    ) {
      // Attendre que les produits soient chargés avant d'initialiser
      if (this.uniqueProducts.length > 0) {
        this.initializeSelectedProducts()
      }
    }
  }

  /**
   * Traite et déduplique les produits pour éviter les doublons dans la liste
   */
  private processAndDeduplicateProducts(): void {
    if (!this.products || !Array.isArray(this.products)) {
      this.uniqueProducts = []
      return
    }

    // Réinitialiser les structures de suivi
    this.productIdSet.clear()
    this.erpProductIdMap.clear()
    const uniqueProducts: any[] = []

    // Traiter chaque produit pour éliminer les doublons
    this.products.forEach((product) => {
      if (!product || !product.id) return

      const productId = product.id

      // Vérifier si ce produit a déjà été ajouté par ID
      if (this.productIdSet.has(productId)) {
        return // Ignorer ce produit car il existe déjà
      }

      // Vérifier s'il existe un doublon par ID ERP
      const erpFields = ['idAbm', 'idNrj', 'idMylink']
      let hasDuplicateErpId = false

      erpFields.forEach((field) => {
        if (product[field]) {
          const erpKey = `${field}:${product[field]}`
          if (this.erpProductIdMap.has(erpKey)) {
            hasDuplicateErpId = true
          } else {
            this.erpProductIdMap.set(erpKey, productId)
          }
        }
      })

      if (!hasDuplicateErpId) {
        // Ajouter le produit à la liste des produits uniques
        uniqueProducts.push(product)
        // Marquer l'ID comme traité
        this.productIdSet.add(productId)
      }
    })

    // Mettre à jour les produits uniques
    this.uniqueProducts = uniqueProducts

    // Mettre à jour l'état de sélection des produits
    this.updateProductsSelectionState()
  }

  /**
   * Met à jour l'état de sélection des produits
   */
  private updateProductsSelectionState(): void {
    // Créer un ensemble des IDs sélectionnés pour recherche rapide
    const selectedIds = new Set(this.selectedProducts.map((p) => p.id))

    // Mettre à jour l'état 'selected' de chaque produit
    this.uniqueProducts.forEach((product) => {
      product.selected = selectedIds.has(product.id)
    })
  }

  private initializeSelectedProducts(): void {
    // Priorité 1: Utiliser les produits de l'intervention originale s'ils sont disponibles
    if (this.interventionProducts && this.interventionProducts.length > 0) {
      this.selectedProducts = [...this.interventionProducts]
      this.updateProductsSelectionState()
      this.emitSelectedProducts()
      this.createProductGroup()
      return
    }

    // Priorité 2: Utiliser les IDs restaurés s'ils sont disponibles
    if (
      this.restoredSelectedProductIds &&
      this.restoredSelectedProductIds.length > 0
    ) {
      this.updateSelectionFromIds()
      return
    }

    // Sinon, réinitialiser la sélection
    this.selectedProducts = []
    this.updateProductsSelectionState()
    this.emitSelectedProducts()
  }

  private updateSelectionFromIds(): void {
    if (
      !this.restoredSelectedProductIds ||
      !this.uniqueProducts ||
      this.uniqueProducts.length === 0
    )
      return

    // Créer une carte d'ID pour une recherche plus efficace
    const productsMap = new Map()
    this.uniqueProducts.forEach((product) =>
      productsMap.set(product.id, product)
    )

    // Sélectionner uniquement les produits qui existent réellement dans notre liste
    this.selectedProducts = this.restoredSelectedProductIds
      .map((id) => productsMap.get(id))
      .filter((product) => product !== undefined)

    // Mettre à jour l'état de sélection dans l'UI
    this.updateProductsSelectionState()

    this.emitSelectedProducts()
    this.createProductGroup()
  }

  private restoreProductGroup(group: any): void {
    if (!group) return

    this.productGroups = [group]
    this.productGroupCreated.emit(group)
  }

  private createProductGroup(): void {
    if (!this.selectedProducts || this.selectedProducts.length === 0) {
      this.productGroups = []
      return
    }

    // Créer un ID unique pour le groupe
    const timestamp = Date.now()
    const productIdsList = this.selectedProducts
      .map((p) => p.id)
      .sort()
      .join('_')
    const groupId = `group_${timestamp}_${productIdsList}`

    // Récupérer la source ERP à partir du premier produit ou de l'affaire
    const erpSource =
      this.selectedProducts[0]?.erpSource || this.affair?.erpSource || 'mylink'

    // Calculer la durée totale
    const totalDuration = this.selectedProducts.reduce(
      (sum, p) => sum + (p.product?.duration || 0),
      0
    )

    // Créer le groupe
    const group = {
      id: groupId,
      products: [...this.selectedProducts],
      productsNames: this.formatGroupName(),
      totalDuration: totalDuration,
      isInitialGroup: true,
      erpSource: erpSource,
    }

    this.productGroups = [group]
    this.productGroupCreated.emit(group)
  }

  // Méthodes UI
  toggleProductSelection(product: any, checked: boolean): void {
    if (checked) {
      // Ajouter le produit s'il n'est pas déjà sélectionné
      if (!this.selectedProducts.some((p) => p.id === product.id)) {
        this.selectedProducts.push(product)
      }
    } else {
      // Retirer le produit de la sélection
      this.selectedProducts = this.selectedProducts.filter(
        (p) => p.id !== product.id
      )
    }

    // Mettre à jour l'état 'selected' du produit dans la liste unique
    const targetProduct = this.uniqueProducts.find((p) => p.id === product.id)
    if (targetProduct) {
      targetProduct.selected = checked
    }

    this.emitSelectedProducts()
    this.createProductGroup()
  }

  isProductSelected(productId: number): boolean {
    return this.selectedProducts.some((p) => p.id === productId)
  }

  selectAll(): void {
    this.selectedProducts = [...this.uniqueProducts]
    this.updateProductsSelectionState()
    this.emitSelectedProducts()
    this.createProductGroup()
  }

  clearSelection(): void {
    this.selectedProducts = []
    this.uniqueProducts.forEach((p) => (p.selected = false))
    this.emitSelectedProducts()
    this.productGroups = []
    this.productGroupCreated.emit(null)
  }

  formatGroupName(): string {
    if (!this.selectedProducts || this.selectedProducts.length === 0) {
      return 'Groupe de produits'
    }

    return (
      this.selectedProducts
        .map((p) => p.product?.name || 'Produit')
        .join(', ')
        .substring(0, 60) + (this.selectedProducts.length > 2 ? '...' : '')
    )
  }

  /**
   * Get an array of ERP entries from the affair's srcErp object
   * Each entry has the format { key: string, value: object }
   */
  getErpEntries(): { key: string; value: any }[] {
    if (!this.affair || !this.affair.srcErp) return []

    return Object.entries(this.affair.srcErp)
      .filter(([key, value]) => value !== null && value !== undefined)
      .map(([key, value]) => ({
        key,
        value,
      }))
  }

  /**
   * Get missing ERP warnings
   * @returns Array of warning messages for missing ERPs
   */
  getMissingErpWarnings(): string[] {
    if (!this.affair || !this.affair.srcErp) return []

    const warnings: string[] = []
    const srcErp = this.affair.srcErp as Record<string, any>

    // Check for missing ABM
    if (!srcErp['abm'] || srcErp['abm'] === null) {
      warnings.push(
        "L'affaire n'existe pas sur l'ERP ABM, par conséquent il n'est pas possible de programmer l'intervention sur un technicien ABM."
      )
    }

    // Check for missing NRJ
    if (!srcErp['nrj'] || srcErp['nrj'] === null) {
      warnings.push(
        "L'affaire n'existe pas sur l'ERP NRJ, par conséquent il n'est pas possible de programmer l'intervention sur un technicien NRJ."
      )
    }

    return warnings
  }

  /**
   * Get the ERP-specific CSS class safely
   * @param key The ERP source key
   * @returns Safe CSS class name
   */
  getErpCssClass(key: string): string {
    return key ? `text-${key}` : ''
  }

  /**
   * Get the ERP-specific logo class safely
   * @param key The ERP source key
   * @returns Safe logo class name
   */
  getLogoClass(key: string): string {
    return key ? `fa-logo-${key}` : ''
  }

  // Envoi des données au parent
  private emitSelectedProducts(): void {
    this.productSelectionChanged.emit([...this.selectedProducts])
  }

  // Méthode d'aide pour le formatage
  getDurationDisplay(product: any): string {
    const duration = product.product?.duration
    if (!duration && duration !== 0) return '?'

    if (duration >= 60) {
      const hours = Math.floor(duration / 60)
      const minutes = duration % 60
      return minutes > 0 ? `${hours}h${minutes}` : `${hours}h`
    }

    return `${duration}min`
  }

  /**
   * Récupère les détails complets de l'affaire pour la fiche bureau
   */
  private getFullAffairDetails(): void {
    if (!this.affair) return

    this.fullAffairLoading = true
    this.http
      .get(environment.planifyApiUrl + '/affairs/' + this.affair.mlRef)
      .subscribe({
        next: (data: any) => {
          this.fullAffairDetails = data
          this.fullAffairLoading = false
        },
        error: (error) => {
          console.error('Error fetching full affair details:', error)
          this.fullAffairLoading = false
        },
      })
  }
}
